// Modules
import { useContext, useEffect, useState } from 'react';
import ReactSelect from 'react-select';

// Components
import { Loading } from '../../common/components';

// Utils
import { TagManager } from '../utils/analytics/TagManager';

// Context
import { UserDetailContext, UserDetailContextProps } from '../../context/userDetail/userDetailContext';
import { GlobalContext, GlobalDetailContextProps } from '../../context/globalContext/globalContext';
import { Button } from 'react-bootstrap';
import FormElement from '../../common/components/FormElement';
import { useSupportFormValidation } from './useSupportFormValidation';
import { submitSupportRequest } from '../../common/api/homeApi';
import { useAuthState } from '../../providers/authProvider';
import { BlankModal } from '../mymessages/modalTemplates/BlankModal';
import { SubmissionFailedModal } from '../mymessages/modalTemplates/SubmissionFailedModal';
import { helpSupportStyles } from './styles/HelpSupportStyles';
import { isTpiUser } from '../../helpers/tpiUser';
import { getAccountDetailsByCustomer } from '../../common/api/accountApi';
import { SomethingWentWrongModal } from '../mymessages/modalTemplates/SomethingWentWrongModal';
import { FAQModal } from '../faq/FAQModal';

type HelpSupportProps = {
  isComponentVisible: boolean;
  handleClose: Function;
};

export const HelpSupport = (props: HelpSupportProps) => {
  const { userDetail } = useContext<UserDetailContextProps>(UserDetailContext);
  const [preference, setPreference] = useState('Email');
  const [phone, setPhone] = useState<string>('');
  const [account, setAccount] = useState<IOption>({ value: '', label: 'Please select ...' });
  const [message, setMessage] = useState<string>('');
  const [customerServiceText, setCustomerServiceText] = useState<string>('your account manager');
  const [customerServiceName, setCustomerServiceName] = useState<string>('');
  const [customerServiceDisplay, setCustomerServiceDisplay] = useState<string>('');
  const [customerServiceEmail, setCustomerServiceEmail] = useState<string>('');
  const [queryType, setQueryType] = useState<IOption>({ value: '', label: 'Please select ...' });
  const [emailCopy, setEmailCopy] = useState('No');
  const [emailCopyBool, setEmailCopyBool] = useState<boolean>(false);
  const [textAreaTouched, setTextAreaTouched] = useState<boolean>(false);
  const [phoneTouched, setPhoneTouched] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [showThankYouModal, setShowThankYouModal] = useState<boolean>(false);
  const [showSomethingWentWrongModal, setShowSomethingWentWrongModal] = useState<boolean>(false);
  const [showFailureModal, setShowFailureModal] = useState<boolean>(false);
  const [showFaqModal, setShowFaqModal] = useState<boolean>(false);

  const authContext = useAuthState();
  const globalContext = useContext<GlobalDetailContextProps>(GlobalContext);

  const modalTitleText = 'Thank you for your request, our support team will be in touch with you shortly.';
  const modalButtonText = 'Ok';

  const tpiUser = isTpiUser() || globalContext.globalDetail.showBrokerLandingPage;
  const isQueryTypeDisabled = account.value === '';

  const accountReferences =
    userDetail.customerAccounts.length > 0
      ? Array.from(userDetail.customerAccounts, (accountReference: any) => {
          return {
            value: accountReference.essAccountID.toString(),
            label: `${accountReference.essAccountID} - ${accountReference.accountName}`,
          };
        })
      : userDetail.customers.length > 0
      ? Array.from(userDetail.customers, (accountReference: any) => {
          return {
            value: accountReference.value.toString(),
            label: `${accountReference.value} - ${accountReference.display}`,
          };
        })
      : [];

  interface IOption {
    value: string;
    label: string;
  }

  const customerQueryOptions = [
    { value: 'Billing query/Dispute', label: 'Billing query/Dispute', email: customerServiceEmail },
    { value: 'Energy services', label: 'Energy services', email: customerServiceEmail },
    { value: 'Meter read/Metering issue', label: 'Meter read/Metering issue', email: 'smart@drax.com' },
    { value: 'Notify new connection', label: 'Notify new connection', email: customerServiceEmail },
    { value: 'Renewable PPA', label: 'Renewable PPA', email: customerServiceEmail },
    { value: 'Renewal/Contract', label: 'Renewal/Contract', email: customerServiceEmail },
    {
      value: 'Request refund/Manage credit allocation',
      label: 'Request refund/Manage credit allocation',
      email: customerServiceEmail,
    },
    { value: 'Request Smart/AMR meter', label: 'Request Smart/AMR meter', email: 'smart@drax.com' },
    { value: 'Sustainability Hub', label: 'Sustainability Hub', email: customerServiceEmail },
    { value: 'Technical issue with Portal', label: 'Technical issue with Portal', email: 'myenergysupply@drax.com' },
    { value: 'Other', label: 'Other', email: customerServiceEmail },
  ];

  const tpiQueryOptions = [
    { value: 'Billing query/Dispute', label: 'Billing query/Dispute', email: customerServiceEmail },
    { value: 'Energy services', label: 'Energy services', email: customerServiceEmail },
    { value: 'Meter read/Metering issue', label: 'Meter read/Metering issue', email: 'smart@drax.com' },
    { value: 'Notify new connection', label: 'Notify new connection', email: customerServiceEmail },
    { value: 'Renewable PPA', label: 'Renewable PPA', email: customerServiceEmail },
    { value: 'Renewal/Contract', label: 'Renewal/Contract', email: customerServiceEmail },
    { value: 'Request Smart/AMR meter', label: 'Request Smart/AMR meter', email: 'smart@drax.com' },
    { value: 'Technical issue with Portal', label: 'Technical issue with Portal', email: 'myenergysupply@drax.com' },
    { value: 'Other', label: 'Other', email: customerServiceEmail },
  ];

  useEffect(() => {
    clearFormFields();
    if (props.isComponentVisible) {
      TagManager.pushData({
        event: 'Raise a query',
        customerAccountReference: userDetail.essCustomerId,
      });
    }

    if (userDetail.customerAccounts.length === 1) {
      handleAccountChange({
        value: userDetail.customerAccounts[0].essAccountID,
        label: `${userDetail.customerAccounts[0].essAccountID} - ${userDetail.customerAccounts[0].accountName}`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isComponentVisible]);

  useEffect(() => {
    if (textAreaTouched) {
      validateMessageInput(message);
    }
    if (phoneTouched) {
      validatePhoneInput(phone);
    }
    if (preference === 'Email') {
      setPhoneTouched(false);
      setPhone('');
      clearPhoneValidation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message, phone, preference]);

  const {
    phoneError,
    messageError,
    validateMessageInput,
    validatePhoneInput,
    clearPhoneValidation,
    clearAllValidation,
  } = useSupportFormValidation();

  const clearFormFields = () => {
    setMessage('');
    setPhone('');
    setAccount({ value: '', label: 'Please select ...' });
    setQueryType({ value: '', label: 'Please select ...' });
    setCustomerServiceText('your account manager');
    setCustomerServiceName('');
    setCustomerServiceDisplay('');
    setCustomerServiceEmail('');
    setEmailCopy('No');
    setEmailCopyBool(false);
    setPreference('Email');
    setTextAreaTouched(false);
    setPhoneTouched(false);
    clearAllValidation();
  };

  const handleEnquiry = async () => {
    setSending(true);

    let request = {
      toAddress: 'myenergysupply@drax.com',
      templateName: 'INC_SupportRequest',
      correspondenceType: 6,
      emailCopy: emailCopyBool,
      placeholderValues: [
        {
          key: '{|reference|}',
          value: account.value,
        },
        {
          key: '{|phoneNumber|}',
          value: preference === 'Email' ? 'N/A' : phone,
        },
        {
          key: '{|preferredContact|}',
          value: preference,
        },
        {
          key: '{|requestDetails|}',
          value: message,
        },
      ],
    };

    TagManager.pushData({
      event: 'Support Submit',
      userType: tpiUser ? 'TPI User' : 'Customer',
      customerAccountReference: account.value,
      queryType: queryType.value,
    });

    const result = await submitSupportRequest(
      process.env.REACT_APP_API_URL,
      request,
      authContext,
      account.value,
      queryType.label
    );

    if (result.status == '200') {
      TagManager.pushData({
        event: 'Success Support Form',
        customerAccountReference: account.value,
      });

      setShowThankYouModal(true);
    } else if (result.status == '500') {
      // 62413 - Remove Support Email Failure Modal (Temporarily)
      setShowSomethingWentWrongModal(true);

      // 62413 - Remove Support Email Failure Modal (Temporarily)
      // TagManager.pushData({
      //   event: 'Failure Support Form',
      // });

      // setShowFailureModal(true);
    }

    setSending(false);
    clearFormFields();
  };

  const loadAccountDetails = async (essCustomerId: string) => {
    const result: Api.IAccountDetailsByCustomer = await getAccountDetailsByCustomer(
      process.env.REACT_APP_API_URL,
      authContext,
      essCustomerId
    );

    if (result) {
      setCustomerServiceEmail(result['customer-service-rep-email']);
      setCustomerServiceName(`${result['customer-service-rep-name']} - ${result['customer-service-rep-email']}`);
      setCustomerServiceDisplay(`${result['customer-service-rep-name']} - ${result['customer-service-rep-email']}`);
    }
  };

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();

    if (preference === 'Email') {
      await handleEnquiry();
    } else {
      validatePhoneInput(phone);
      if (phone && !phoneError) {
        await handleEnquiry();
      }
    }
  };

  const handleAccountChange = async (account: IOption) => {
    setAccount(account);
    setQueryType({ value: '', label: 'Please select ...' });
    setCustomerServiceDisplay('');
    loadAccountDetails(account.value);
  };

  const handleQueryTypeChange = async (query: IOption) => {
    if (query.value === 'Meter read/Metering issue' || query.value === 'Request Smart/AMR meter') {
      setCustomerServiceDisplay('smart@drax.com');
      setCustomerServiceText('your account manager');
    } else if (query.value === 'Technical issue with Portal') {
      setCustomerServiceDisplay('myenergysupply@drax.com');
      setCustomerServiceText('our support team');
    } else {
      setCustomerServiceDisplay(customerServiceName);
      setCustomerServiceText('your account manager');
    }

    setQueryType(query);
  };

  const handleEmailCopyPreference = (preference: string) => {
    setEmailCopy(preference);

    if (preference === 'Yes') {
      setEmailCopyBool(true);
    } else {
      setEmailCopyBool(false);
    }
  };

  const handleThankYouOnHide = (event: any) => {
    setShowThankYouModal(false);
    props.handleClose();
  };

  const handleSomethingWentWrongOnHide = (event: any) => {
    setShowSomethingWentWrongModal(false);
    props.handleClose();
  };

  const handleFailureOnHide = (event: any) => {
    setShowFailureModal(false);
    props.handleClose();
  };

  const submitButtonDisabled =
    message.length < 10 ||
    (preference === 'Phone' && phone === '') ||
    phoneError !== '' ||
    sending ||
    account.value === '' ||
    queryType.value === '';

  const handleFAQclicked = () => {
    setShowFaqModal(true);
  };

  return (
    <div className={`support-popup ${props.isComponentVisible && !showFaqModal ? '' : 'hidden'}`}>
      <h5>Query and Support</h5>
      <h6 className='pt-1 supportText'>
        Send us a message with your details and we'll get back to you as soon as we are able to. For quick answers to
        common questions, please visit our{' '}
        <a onClick={handleFAQclicked} rel='noopener noreferrer'>
          FAQs
        </a>{' '}
        page.
      </h6>

      <div>
        <FormElement.Radio
          id='support-contact-preference'
          className='pt-3 mb-0 ml-0 label'
          label={'How do you wish to be contacted?'}
          name='contactPref'
          elements={['Email', 'Phone']}
          value={preference}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setPreference(e.target.value);
          }}
        />
      </div>

      <form onSubmit={handleSubmit}>
        <div className=''>Your email address</div>
        <div className='mb-0 ml-0'>
          <FormElement.Input
            id='support-email-address'
            className='c-form__input locked-input'
            label={''}
            name='email'
            type='email'
            value={userDetail.emailAddress || ''}
            onChange={() => {}}
            errorMessage=''
            disabled={true}
            locked={true}
          />
        </div>

        {preference === 'Phone' && (
          <div>
            <div>Your phone number</div>
            <FormElement.Input
              id='support-phone-number'
              className='c-form__input'
              name='phone'
              type='number'
              value={phone}
              errorMessage={phoneError}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setPhoneTouched(true);
                setPhone(e.target.value);
              }}
              onBlur={(e: React.ChangeEvent<HTMLInputElement>) => validatePhoneInput(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                const invalidKeys = ['e', 'E', '+', '.', '-'];

                if (invalidKeys.includes(e.key)) {
                  e.preventDefault();
                }
              }}
            />
          </div>
        )}

        <div>Select Account the query relates to</div>
        <div className='mb-3'>
          <ReactSelect
            id='account-ref'
            autoFocus={false}
            blurInputOnSelect={true}
            placeholder={'Please select...'}
            value={account}
            styles={helpSupportStyles}
            options={accountReferences}
            onChange={handleAccountChange}
          />
        </div>

        <div>Type of query</div>
        <div className='mb-3'>
          <ReactSelect
            id='query-type'
            autoFocus={false}
            blurInputOnSelect={true}
            placeholder={'Please select...'}
            value={queryType}
            styles={helpSupportStyles}
            options={tpiUser ? tpiQueryOptions : customerQueryOptions}
            onChange={handleQueryTypeChange}
            isDisabled={isQueryTypeDisabled}
          />
        </div>

        <div className='pt-1'>
          <FormElement.Textarea
            id='support-user-message'
            placeholder={
              'How can we help? Provide as much detail as you can. Include any additional contact names and numbers.'
            }
            className='c-form__input no-resize'
            label={''}
            name='message'
            value={message}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              setTextAreaTouched(true);
              setMessage(e.target.value);
            }}
            onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) => {
              validateMessageInput(e.target.value);
            }}
            errorMessage={messageError}
            rows={5}
          />
        </div>

        <div className=''>{`This query will be sent to ${customerServiceText}`}</div>
        <div className='mb-0 ml-0'>
          <FormElement.Input
            id='support-account-manager'
            className='c-form__input locked-input'
            label={''}
            name='accountManager'
            type='text'
            value={customerServiceDisplay}
            onChange={() => {}}
            errorMessage=''
            disabled={true}
            locked={true}
          />
        </div>

        <div className='container p-0'>
          <div className='left'>Would you like a copy of this submission sent to your email address?</div>
          <div className='right'>
            <FormElement.Radio
              id='support-email-copy'
              className='mb-0 ml-0 label'
              label={''}
              name='emailCopy'
              elements={['Yes', 'No']}
              value={emailCopy}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                handleEmailCopyPreference(e.target.value);
              }}
            />
          </div>
        </div>

        <div className='pb-0'>
          <Button className='c-form__button' onClick={handleSubmit} disabled={submitButtonDisabled}>
            Submit
            {sending && <Loading overlay nowait />}
          </Button>
        </div>
      </form>

      <BlankModal
        show={showThankYouModal}
        onHide={handleThankYouOnHide}
        titleText={modalTitleText}
        buttonText={modalButtonText}
      ></BlankModal>

      <SomethingWentWrongModal
        show={showSomethingWentWrongModal}
        onHide={handleSomethingWentWrongOnHide}
      ></SomethingWentWrongModal>

      <SubmissionFailedModal show={showFailureModal} onHide={handleFailureOnHide}></SubmissionFailedModal>

      <FAQModal
        show={showFaqModal}
        onClose={() => {
          setShowFaqModal(false);
        }}
      ></FAQModal>
    </div>
  );
};
